/**
 * English UI strings — the source of truth. Every other locale file must contain exactly these keys
 * (enforced by `pnpm i18n:check`). Messages use a small ICU subset: {name}, plural and select.
 */
export const en: Record<string, string> = {
  // App
  "app.name": "{brand}",
  "app.tagline": "Live tabletop exercises for the incidents that start with a person.",
  "app.skipToContent": "Skip to content",

  // Navigation & common actions
  "nav.home": "Home",
  "nav.host": "Facilitator console",
  "nav.join": "Join a session",
  "nav.settings": "Reading & display settings",
  "action.continue": "Continue",
  "action.back": "Back",
  "action.cancel": "Cancel",
  "action.close": "Close",
  "action.copy": "Copy",
  "action.copied": "Copied",
  "action.save": "Save",
  "action.retry": "Try again",
  "action.signOut": "Sign out",
  "action.open": "Open",
  "action.done": "Done",
  "action.confirm": "Confirm",
  "action.delete": "Delete",
  "action.refresh": "Refresh",

  // Connection / session status
  "status.connecting": "Connecting…",
  "status.reconnecting": "Connection lost. Reconnecting…",
  "status.connected": "Connected",
  "status.offline": "You are offline",
  "status.lobby": "Lobby",
  "status.live": "Live",
  "status.debrief": "Debrief",
  "status.ended": "Ended",
  "status.paused": "Paused by the facilitator",

  // Accessibility panel
  "a11y.title": "Reading & display",
  "a11y.intro": "Choose what is easiest for you to read. Settings are saved on this device only.",
  "a11y.group.text": "Text",
  "a11y.font": "Typeface",
  "a11y.font.atkinson": "Clear sans (default)",
  "a11y.font.atkinson.help": "Atkinson Hyperlegible — designed for low vision, distinct letterforms.",
  "a11y.font.lexend": "Wide spacing",
  "a11y.font.lexend.help": "Lexend — wider letters and spacing, designed for reading fluency.",
  "a11y.font.opendyslexic": "Dyslexia-style",
  "a11y.font.opendyslexic.help": "OpenDyslexic — heavy baselines to reduce letter flipping.",
  "a11y.font.system": "System font",
  "a11y.font.system.help": "Whatever your device uses everywhere else.",
  "a11y.size": "Text size",
  "a11y.size.default": "Default",
  "a11y.size.large": "Large",
  "a11y.size.xl": "Extra large",
  "a11y.spacing": "Spacing",
  "a11y.spacing.default": "Default",
  "a11y.spacing.comfortable": "Comfortable",
  "a11y.spacing.reading": "Reading mode",
  "a11y.spacing.reading.help":
    "Larger text, 1.8 line height, wider letter and word spacing, bold instead of italics, no all-caps.",
  "a11y.group.colour": "Colour & motion",
  "a11y.theme": "Colours",
  "a11y.theme.auto": "Match device",
  "a11y.theme.light": "Light",
  "a11y.theme.dark": "Dark",
  "a11y.theme.cream": "Cream paper",
  "a11y.motion": "Motion",
  "a11y.motion.auto": "Match device",
  "a11y.motion.reduced": "Reduced",
  "a11y.motion.full": "Full",
  "a11y.contrast": "Contrast",
  "a11y.contrast.normal": "Normal",
  "a11y.contrast.high": "High",
  "a11y.language": "Language",
  "a11y.preview": "The quick brown fox jumps over the lazy dog. 0O 1lI 5S 8B.",
  "a11y.reset": "Reset to defaults",

  // Landing page
  "landing.eyebrow": "Live tabletop exercises",
  "landing.hero.title": "It starts with a call, an invoice, a login.",
  "landing.hero.body":
    "Your whole room decides what happens next. {brand} turns a security tabletop exercise into a live, multiplayer decision game: everyone joins from their phone, takes a role, and votes on every move while the incident unfolds on the big screen.",
  "landing.hero.note": "No accounts for players · Runs in any browser · Twelve languages",
  "landing.cta.host": "Run an exercise",
  "landing.cta.join": "I have a code",
  "landing.hero.demoLabel":
    "A moment from The Password Reset: the call, the text with the code, and the room's vote",

  // Figures band
  "landing.stats.title": "The library at a glance",
  "landing.stats.scenarios": "Scenarios",
  "landing.stats.decisions": "Graded decisions",
  "landing.stats.roles": "Roles to fill",
  "landing.stats.languages": "Languages",

  // How a session runs
  "landing.how.title": "How a session runs",
  "landing.how.note": "Forty to sixty minutes, invitation to report",
  "landing.how.1.title": "Pick a scenario",
  "landing.how.1.body":
    "Six incident-derived scripts, from a help-desk identity fraud to a Friday-night ransomware call. Choose the threat you actually need to rehearse.",
  "landing.how.2.title": "Everyone joins by phone",
  "landing.how.2.body":
    "Scan the QR code or open the link. No accounts, no app, no install. Take a role or watch from the side.",
  "landing.how.3.title": "Decide together, on the clock",
  "landing.how.3.body":
    "Each role sees only what it would really see. The room votes, the story branches on the result, and the attacker's progress is tracked as a variable.",
  "landing.how.4.title": "Debrief with evidence",
  "landing.how.4.body":
    "Capability scores, badges, the red flags nobody called, and a printable after-action report the moment the session ends.",

  // What it exercises
  "landing.threats.title": "What it exercises",
  "landing.threats.note": "Every scenario starts with a person and becomes an incident",
  "landing.threats.social.title": "Social engineering and pretext",
  "landing.threats.social.body":
    "Voice phishing, help-desk impersonation, fake IT support, and the callback nobody makes because the caller sounds busy.",
  "landing.threats.deepfake.title": "Deepfake and AI-assisted fraud",
  "landing.threats.deepfake.body":
    "A cloned executive voice on a payment approval, and the verification habit that has to survive hearing someone you know.",
  "landing.threats.bec.title": "Business email compromise",
  "landing.threats.bec.body":
    "Supplier payee changes, funds-transfer fraud, and the narrow window in which a wire can still be recalled.",
  "landing.threats.ransomware.title": "Ransomware and extortion",
  "landing.threats.ransomware.body":
    "Backups destroyed first, data taken before encryption, and a containment order that decides both the outage and the claim.",
  "landing.threats.thirdparty.title": "Third-party and SaaS compromise",
  "landing.threats.thirdparty.body":
    "A vendor's stolen integration tokens, your customer data, and a notification clock that started without you.",
  "landing.threats.identity.title": "Identity and access abuse",
  "landing.threats.identity.body":
    "MFA resets, session theft, privileged accounts, and the controls that take the phone call out of the path entirely.",

  // Capabilities
  "landing.features.title": "Built for the way exercises actually run",
  "landing.features.roles.title": "Role-private briefings",
  "landing.features.roles.body":
    "The help desk sees the ticket, the SOC sees the alert, finance sees the invoice, the CISO sees nothing yet — just like the real day.",
  "landing.features.branching.title": "Consequences that branch",
  "landing.features.branching.body":
    "A hasty reset hands over the account; a callback stops it. Scenario variables track how far the attacker got and the story answers for it.",
  "landing.features.injects.title": "Facilitator injects",
  "landing.features.injects.body":
    "Drop an angry callback, a journalist, a regulator or the after-hours line into the room whenever it goes quiet.",
  "landing.features.report.title": "Instant after-action report",
  "landing.features.report.body":
    "Decisions, votes, timeline, red flags and recommendations — shareable by link, printable to a proper PDF.",
  "landing.features.access.title": "Accessible by design",
  "landing.features.access.body":
    "Dyslexia-friendly typefaces, a reading mode, high contrast, reduced motion, extendable timers and twelve languages.",
  "landing.features.scale.title": "Rooms of five or five hundred",
  "landing.features.scale.body":
    "One isolated room per session on Cloudflare's edge. Phones that drop off keep their seat, their role and their score.",

  // Report showcase
  "landing.report.title": "The after-action report writes itself",
  "landing.report.body":
    "The document the exercise exists to produce — built while you play, and ready before anyone leaves the room.",
  "landing.report.1": "A readiness score and a capability profile across five dimensions.",
  "landing.report.2": "Every decision with the room's vote, what it cost and why.",
  "landing.report.3": "Red flags missed, recommendations, and who was in the room.",
  "landing.report.4": "Share by link or print to PDF — the print stylesheet is a real document.",
  "landing.report.figureLabel": "Readiness",

  // Audience tracks
  "landing.tracks.title": "Three rooms it is written for",
  "landing.tracks.note": "Same mechanics, different roles, vocabulary and pace",
  "landing.tracks.count": "{count, plural, =0 {Coming soon} one {# scenario} other {# scenarios}}",
  "audience.workplace": "Workplace",
  "audience.workplace.who": "Security, IT, finance and leadership teams",
  "audience.workplace.body":
    "Help-desk identity fraud, deepfake payment approvals, supplier payee changes, ransomware and third-party breaches — chosen from claims data and run with the roles a real incident involves.",
  "audience.community": "Community and seniors",
  "audience.community.who": "Older adults, families, libraries, banks and carers",
  "audience.community.body":
    "The impostor calls that cost people over sixty billions a year — the fake bank, the fake grandchild, the fake agent — played from the kitchen table, with the family, the teller and the neighbour in the room.",
  "audience.youth": "Young people",
  "audience.youth.who": "Ages 12 to 16, with a teacher, club leader or parent",
  "audience.youth.body":
    "The DM from a friend's stolen account, the free-skins login page, the threat that follows, and the offer to 'borrow' a bank account — in their words, with their friends as the roles.",

  // Library
  "landing.scenarios.title": "The library",
  "landing.scenarios.note": "Modelled on public incidents, claims data and fraud reporting",

  // Screenshot tour
  "landing.tour.title": "See it running",
  "landing.tour.note": "Real frames from a simulated session",
  "landing.tour.cta": "Take the tour",
  "landing.tour.example": "Read an example report",
  "tour.eyebrow": "The tour",
  "tour.title": "A session, frame by frame",
  "tour.intro":
    "Every image below is the real product, captured from a session run with simulated participants: the projector, the phones, the facilitator console and the report it produced.",
  "tour.example": "Open the example report",
  "tour.note":
    "Frames are regenerated from the current build with `pnpm tour`. The people in them are simulated participants; the report is the fictional example at /r/example.",
  "tour.surface.screen": "Big screen",
  "tour.surface.phone": "Phone",
  "tour.surface.desktop": "Facilitator console",
  "tour.surface.report": "After-action report",
  "tour.screen-lobby.title": "The room fills",
  "tour.screen-lobby.body":
    "The projector shows the code and the QR. Everyone joins from their own phone; avatars appear as they arrive.",
  "tour.screen-lobby.alt":
    "Projector lobby view with a large QR code, a six-character join code and a row of participant avatars.",
  "tour.phone-join.title": "Join in thirty seconds",
  "tour.phone-join.body":
    "A name, an avatar, a role if you want one. No account, no app. An optional email appears masked to the facilitator only.",
  "tour.phone-join.alt": "Phone join form with a name field, avatar choices and a list of roles to pick.",
  "tour.screen-call.title": "The call comes in",
  "tour.screen-call.body":
    "The facilitator reveals the transcript line by line while the room listens. Each role's phone shows only what that role would know.",
  "tour.screen-call.alt":
    "Projector view of an incoming call transcript with caller details and a live waveform.",
  "tour.phone-vote.title": "Decide on the clock",
  "tour.phone-vote.body":
    "Each decision opens a timed vote. Options are graded but the grades stay hidden until the reveal, and a private role note sits above the choices.",
  "tour.phone-vote.alt":
    "Phone showing a decision prompt, a countdown ring, a private role note and four options to vote on.",
  "tour.console.title": "The facilitator's desk",
  "tour.console.body":
    "Stage, controls, players and roles, injects, the scenario map and every link in one place. Simulated participants are marked and can be added from here.",
  "tour.console.alt":
    "Facilitator console with the current decision, vote progress and the list of participants.",
  "tour.screen-reveal.title": "The room decided",
  "tour.screen-reveal.body":
    "The vote closes, the choice is graded, the consequences are read out, and the story branches on the result.",
  "tour.screen-reveal.alt":
    "Projector view of a revealed decision with the winning option, its quality and the vote distribution.",
  "tour.phone-result.title": "Points, explained",
  "tour.phone-result.body":
    "Every player sees what the room chose, what they chose, and how their points break down: decision, speed, streak, role.",
  "tour.phone-result.alt":
    "Phone showing the room's choice, the player's points breakdown and the vote bars.",
  "tour.screen-debrief.title": "The debrief",
  "tour.screen-debrief.body":
    "A readiness score, a capability radar and the team's badges on the wall while the facilitator runs the hot wash.",
  "tour.screen-debrief.alt":
    "Projector debrief view with a capability radar, a large readiness score and team badges.",
  "tour.report.title": "The after-action report",
  "tour.report.body":
    "Written the moment the session ends: summary, capability profile, every decision with its vote, red flags, recommendations, who was in the room. Shareable by link, printable to PDF.",
  "tour.report.alt": "The first screen of the after-action report: masthead, readiness ring and key figures.",
  "tour.phone-rtl.title": "Twelve languages, right to left included",
  "tour.phone-rtl.body":
    "Players choose their own language on their phones. Arabic runs right to left; the display face steps aside for scripts it does not cover.",
  "tour.phone-rtl.alt": "Phone join page in Arabic, laid out right to left.",

  // Closing
  "landing.close.title": "Run the incident before it runs you.",
  "landing.close.body":
    "Pick a scenario, put the code on the screen, and find out what your organisation actually does in the first twenty minutes.",
  "landing.footer":
    "{brand} is an open tabletop-exercise platform. Scenarios are fictional composites modelled on public incident reports and claims data; any resemblance to a specific organisation is unintended.",

  // Threat-class tags shown on scenario cards
  "tag.vishing": "Voice phishing",
  "tag.deepfake": "Deepfake",
  "tag.bec": "Email compromise",
  "tag.payment-fraud": "Payment fraud",
  "tag.ransomware": "Ransomware",
  "tag.extortion": "Extortion",
  "tag.third-party": "Third party",
  "tag.supply-chain": "Supply chain",
  "tag.saas": "SaaS",
  "tag.identity": "Identity",
  "tag.help-desk": "Help desk",
  "tag.remote-access": "Remote access",
  "tag.data-breach": "Data breach",
  "tag.impostor": "Impostor scam",
  "tag.tech-support": "Tech-support scam",
  "tag.family-emergency": "Family emergency",
  "tag.account-takeover": "Account takeover",
  "tag.phishing": "Phishing",
  "tag.money-mule": "Money mule",

  // Join flow
  "join.title": "Join a session",
  "join.codeLabel": "Session code",
  "join.codePlaceholder": "ABC123",
  "join.enter": "Find session",
  "join.invalidCode": "That doesn't look like a session code. Codes are six letters and numbers.",
  "join.notFound": "No session found with that code. Check with your facilitator.",
  "join.ended": "This session has ended.",
  "join.loading": "Finding your session…",
  "join.nameLabel": "Your name",
  "join.namePlaceholder": "How should the room see you?",
  "join.avatarLabel": "Pick an avatar",
  "join.emailLabel": "Email (optional)",
  "join.emailHelp":
    "The facilitator sees only a masked version — j•••n@example.com — in the after-action report. Your address itself is never stored.",
  "join.emailHelpSeat":
    "Your invitation already carries an address. Add one here only if you would rather be listed under a different one; either way the report shows it masked.",
  "join.emailPlaceholder": "you@example.com",
  "join.emailInvalid": "That does not look like an email address. Correct it or leave the field empty.",
  "join.privacyNote":
    "The facilitator's report lists your city, region, country, network provider and device type. Your IP address is never stored.",
  "join.roleLabel": "Choose a role",
  "join.roleHelp": "Roles get private briefings. You can also play without one.",
  "join.roleAny": "No role — I'll vote on everything",
  "join.roleTaken": "{taken} of {max} taken",
  "join.roleCount": "{count, plural, =0 {No one yet} one {# player} other {# players}}",
  "join.roleFull": "Full",
  "join.seatTitle": "You have a reserved seat",
  "join.seatBody": "The facilitator reserved this seat for {label}. Your role is set.",
  "join.asPlayer": "Join and play",
  "join.asViewer": "Just watch",
  "join.joining": "Joining…",
  "join.full": "This session is full.",
  "join.rateLimited": "Too many attempts. Wait a moment and try again.",
  "join.error": "Couldn't join. {message}",
  "join.minutes": "{count, plural, one {# minute} other {# minutes}}",
  "join.playersIn":
    "{count, plural, =0 {No players yet} one {# player in the room} other {# players in the room}}",

  // Player experience
  "player.lobby.title": "You're in",
  "player.lobby.waiting": "Waiting for the facilitator to start. Keep this screen open.",
  "player.lobby.youAre": "You are",
  "player.lobby.noRole": "No role",
  "player.lobby.changeRole": "Change role",
  "player.lobby.changeName": "Change name",
  "player.brief": "Your briefing",
  "player.objectives": "Your objectives",
  "player.roleNote": "Only you can see this",
  "player.vote.title": "Your vote",
  "player.vote.help": "Tap an option. You can change your mind until the timer runs out.",
  "player.vote.cast": "Vote recorded",
  "player.vote.changed": "Vote changed",
  "player.vote.locked": "Voting is closed",
  "player.vote.notEligible": "This decision is for {roles}. You can watch the result.",
  "player.vote.waitingReveal": "Votes are in. Waiting for the facilitator to reveal.",
  "player.vote.timeLeft": "{seconds, plural, one {# second left} other {# seconds left}}",
  "player.vote.closesSoon": "Closing soon",
  "player.vote.progress": "{cast} of {eligible} voted",
  "player.result.roomChose": "The room chose",
  "player.result.youChose": "You chose",
  "player.result.points": "+{points} points",
  "player.result.noVote": "You didn't vote this round.",
  "player.result.breakdown.base": "Decision",
  "player.result.breakdown.speed": "Speed",
  "player.result.breakdown.streak": "Streak",
  "player.result.breakdown.role": "Your role's call",
  "player.poll.title": "Quick poll",
  "player.poll.help": "No right answer — the room just wants to know.",
  "player.poll.thanks": "Thanks — watch the screen for the result.",
  "player.call.title": "Incoming call",
  "player.call.follow": "Follow along on the big screen. Your role notes are below.",
  "player.narrative.follow": "Read along with the room.",
  "player.reveal.title": "What just happened",
  "player.debrief.title": "Debrief",
  "player.debrief.yourScore": "Your score",
  "player.debrief.rank": "#{rank} of {total}",
  "player.debrief.badges": "Your badges",
  "player.debrief.noBadges": "No badges this time — every exercise is a fresh start.",
  "player.debrief.team": "Team readiness",
  "player.ended.title": "Session over",
  "player.ended.body": "Thanks for playing. Your facilitator has the full after-action report.",
  "player.kicked.title": "You were removed",
  "player.kicked.body": "The facilitator removed you from this session.",
  "player.react.label": "React",
  "player.score": "Score",
  "player.streak": "Streak",
  "player.waitingScreen": "Watch the screen",
  "player.branchWait": "The story is moving…",

  // Artefacts (the in-world documents on narrative nodes)
  "artifact.kind.email": "Email",
  "artifact.kind.sms": "Text message",
  "artifact.kind.chat": "Chat",
  "artifact.kind.ticket": "Ticket",
  "artifact.kind.news": "News",
  "artifact.kind.memo": "Memo",
  "artifact.kind.alert": "Alert",
  "artifact.from": "From",
  "artifact.to": "To",
  "artifact.date": "Date",
  "artifact.subject": "Re",
  "artifact.requester": "Requester",

  // Big screen / viewer
  "screen.join.title": "Join on your phone",
  "screen.join.code": "Code",
  "screen.join.scan": "Scan to join",
  "screen.join.or": "or go to",
  "screen.players": "{count, plural, =0 {No players yet} one {# player} other {# players}}",
  "screen.viewers": "{count, plural, one {# viewer} other {# viewers}}",
  "screen.waitingHost": "Waiting for the facilitator",
  "screen.vote.open": "Vote now on your phone",
  "screen.vote.closed": "Voting closed",
  "screen.vote.progress": "{cast} of {eligible} voted",
  "screen.vote.hidden": "Results hidden until the reveal",
  "screen.vote.result": "The room decided",
  "screen.results": "Results",
  "screen.leaderboard": "Leaderboard",
  "screen.readiness": "Team readiness",
  "screen.dimensions": "Capability scores",
  "screen.paused": "Paused",
  "screen.injectFired": "Inject: {title}",
  "screen.callFrom": "Incoming call",
  "screen.spoofed": "Caller ID can be spoofed",
  "screen.redFlags": "Red flags",
  "screen.tells": "Techniques used",
  "screen.lessons": "Take-aways",
  "screen.debrief.title": "Debrief",
  "screen.badges.team": "Team badges",
  "screen.poll.title": "Quick poll",
  "screen.timeline": "Path taken",
  "screen.attackerProgress": "Attacker progress",
  "screen.forbidden": "This screen link is not valid. Ask the facilitator for a new one.",
  "screen.unlock": "Screen key",
  "screen.unlockHelp": "Paste the key from the facilitator console.",
  "screen.scrollable": "Scene content, scrollable",
  "screen.variables": "Situation",

  // Facilitator: sign-in
  "host.login.title": "Facilitator sign-in",
  "host.login.body": "Enter your work email. We'll send a one-time link and a six-digit code.",
  "host.login.email": "Email address",
  "host.login.send": "Send sign-in link",
  "host.login.sent": "Check your inbox",
  "host.login.sentBody": "We sent a link to {email}. It works once and expires in {minutes} minutes.",
  "host.login.codeLabel": "Or enter the six-digit code",
  "host.login.verifyCode": "Sign in with code",
  "host.login.devLink": "Development mode: no email service is configured. Use this link:",
  "host.login.error": "Couldn't send the link. {message}",
  "host.login.invalid": "That link or code is invalid or has expired. Request a new one.",
  "host.verify.title": "Confirm sign-in",
  "host.verify.body": "Press the button to finish signing in. Email scanners can't do this for you.",
  "host.verify.confirm": "Confirm sign-in",
  "host.verify.confirming": "Signing you in…",
  "host.verify.missing": "This link is missing its token. Open it exactly as it arrived.",

  // Facilitator: dashboard
  "host.dash.title": "Your sessions",
  "host.dash.new": "New session",
  "host.dash.empty": "No sessions yet. Create one to get a join code and a big-screen link.",
  "host.dash.players": "{count, plural, =0 {No players} one {# player} other {# players}}",
  "host.dash.readiness": "Readiness {score}",
  "host.dash.created": "Created {when}",
  "host.dash.openConsole": "Open console",
  "host.dash.report": "Report",

  // Facilitator: new session
  "host.new.title": "New session",
  "host.new.pickScenario": "Choose a scenario",
  "host.new.sessionTitle": "Session name",
  "host.new.sessionTitleHelp": "Shown to players and on the report. Defaults to the scenario title.",
  "host.new.settings": "Room settings",
  "host.new.create": "Create session",
  "host.new.creating": "Creating…",
  "host.settings.liveTally": "Show live vote counts",
  "host.settings.liveTally.help": "Off: results appear only at the reveal (recommended for honest votes).",
  "host.settings.allowViewers": "Allow viewers",
  "host.settings.allowViewers.help": "Anyone with the watch link can follow without voting.",
  "host.settings.openRoleSelection": "Players pick their own roles",
  "host.settings.openRoleSelection.help": "Off: you assign roles from the console or with reserved seats.",
  "host.settings.autoAdvance": "Auto-advance narrative slides",
  "host.settings.autoAdvance.help": "Seconds before narrative slides move on by themselves. 0 = manual.",
  "host.settings.locale": "Content language for the big screen",
  "host.settings.locale.help": "Players choose their own UI language on their phones.",
  "host.settings.showLeaderboard": "Show the leaderboard between decisions",
  "host.settings.showSidebar": "Show the projector sidebar",
  "host.settings.showSidebar.help":
    "Join code, players, leaderboard and readiness beside the stage. Off gives the stage the full width; the lobby never shows it.",
  "host.settings.reactions": "Allow emoji reactions",
  "host.new.group.mine": "Your scenarios",
  "host.new.group.mine.who": "Copies you have made and scenarios you have written",
  "host.new.group.community": "Community",
  "host.new.group.community.who": "Shared by other facilitators and reviewed before listing",
  "host.new.group.shared": "Shared with you by link",
  "host.new.useDraft": "Run the unpublished draft",
  "host.new.useDraft.help": "Off: the session runs the published copy (v{version}).",
  "host.nav.sessions": "Sessions",
  "host.nav.build": "Scenarios",
  "host.new.demo": "Demo mode",
  "host.new.demo.help":
    "Create the session with simulated participants already in the roles, so you can run the whole exercise on your own.",

  // Simulated participants
  "sim.label": "Simulated participant",
  "host.sim.title": "Simulated participants",
  "host.sim.help":
    "Artificial participants fill the roles and vote, answer polls and react on their own — for demonstrating the exercise, not for scoring a real team. They are labelled everywhere, including the report.",
  "host.sim.count": "How many",
  "host.sim.temperament": "Temperament",
  "host.sim.temperament.cautious": "Cautious",
  "host.sim.temperament.cautious.help": "Mostly the recommended course, with the odd slip.",
  "host.sim.temperament.mixed": "Mixed",
  "host.sim.temperament.mixed.help": "A realistic room: good instincts and some hasty calls.",
  "host.sim.temperament.rushed": "Rushed",
  "host.sim.temperament.rushed.help": "Under pressure, and it shows in the votes.",
  "host.sim.pace": "Pace",
  "host.sim.pace.natural": "Natural — votes arrive across the window",
  "host.sim.pace.quick": "Quick — everyone votes in the first seconds",
  "host.sim.add": "Add simulated participants",
  "host.sim.removeAll": "Remove all simulated ({count})",

  // Facilitator: console
  "host.console.title": "Console",
  "host.console.start": "Start exercise",
  "host.console.next": "Next",
  "host.console.back": "Back",
  "host.console.openVote": "Open vote",
  "host.console.closeVote": "Close vote now",
  "host.console.extend": "+30 s",
  "host.console.force": "Set outcome…",
  "host.console.forceHelp": "Override the vote and choose the outcome yourself.",
  "host.console.revealLine": "Next line",
  "host.console.revealAll": "Show whole call",
  "host.console.pause": "Pause",
  "host.console.resume": "Resume",
  "host.console.end": "End session",
  "host.console.endConfirm": "End the session for everyone and write the report?",
  "host.console.reopen": "Reset to lobby",
  "host.console.reopenConfirm": "Reset the exercise? Players stay, scores and votes are cleared.",
  "host.console.injects": "Injects",
  "host.console.noInjects": "No injects available right now.",
  "host.console.fireInject": "Fire",
  "host.console.players": "Players",
  "host.console.noPlayers": "Nobody has joined yet. Share the join link or put the code on the screen.",
  "host.console.assignRole": "Assign role",
  "host.console.kick": "Remove",
  "host.console.kickConfirm": "Remove {name} from the session?",
  "host.console.rename": "Rename",
  "host.console.seats": "Reserved seats",
  "host.console.seatsHelp": "Create named seats with a fixed role and send each person their own link.",
  "host.console.addSeat": "Add seat",
  "host.console.seatLabel": "Name or label",
  "host.console.seatRole": "Role",
  "host.console.seatEmail": "Email (optional)",
  "host.console.seatEmailHelp":
    "Appears masked in the after-action report so you can tell participants apart. Other players never see it.",
  "host.console.seatEmailPlaceholder": "person@company.com",
  "host.console.seatEmailInvalid": "Enter a valid email address or leave the field empty.",
  "host.console.seatClaimed": "Claimed",
  "host.console.seatOpen": "Not yet used",
  "host.console.links": "Links",
  "host.console.joinLink": "Join link",
  "host.console.screenLink": "Big screen",
  "host.console.watchLink": "Viewer link",
  "host.console.reportLink": "Report",
  "host.console.openScreen": "Open big screen",
  "host.console.facilitatorNotes": "Facilitator notes",
  "host.console.roleNotes": "Role-private notes",
  "host.console.map": "Scenario map",
  "host.console.goto": "Jump here",
  "host.console.gotoConfirm": 'Jump to "{title}"? Any open vote is discarded.',
  "host.console.timeline": "Timeline",
  "host.console.votesProgress": "{cast} of {eligible} voted",
  "host.console.notVoted": "Not voted yet",
  "host.console.connected": "connected",
  "host.console.disconnected": "disconnected",
  "host.console.screens": "{count, plural, =0 {No screens} one {# screen} other {# screens}}",
  "host.console.viewers": "{count, plural, =0 {No viewers} one {# viewer} other {# viewers}}",
  "host.console.report": "After-action report",
  "host.console.tally": "Live tally",
  "host.console.tallyHidden": "Hidden from players until reveal",
  "host.console.currentNode": "Now showing",
  "host.console.settings": "Settings",
  "host.console.status": "Status",
  "host.console.wsFallback": "Live connection unavailable — commands are sent over HTTP.",
  "host.console.debriefReady": "Debrief reached. End the session to write the after-action report.",
  "host.console.copyAll": "Copy all links",
  "host.console.deleteSession": "Delete session",
  "host.console.deleteConfirm": "Delete this session and its report permanently?",

  // Facilitator: report
  "host.report.title": "After-action report",
  "host.report.open": "Open printable report",
  "host.report.share": "Share link (read-only)",
  "host.report.json": "Download JSON",
  "host.report.notYet": "The report is written when the session ends.",
  "host.report.live": "Live preview — the session is still running.",

  // Builder (the scenario editor at /build)
  "build.status.draft": "Draft",
  "build.status.published": "Published",
  "build.status.publishedChanges": "Published · unpublished edits",
  "build.basedOn": "Based on {title}",
  "build.open": "Open in the editor",
  "build.copyAndEdit": "Copy and edit",
  "build.copying": "Copying…",
  "build.status.publishedVersion": "Published v{version}",
  "build.visibility.private": "Private",
  "build.visibility.unlisted": "By link",
  "build.visibility.listed": "Listed",
  "build.visibility.pending": "Listed · pending",
  "build.copy": "Copy",
  "build.edit": "Edit",
  "build.export": "Export",
  "build.runTest": "Run a test session",
  "build.delete": "Delete scenario",
  "build.deleteConfirm":
    "Delete “{title}”? Sessions and reports that used it keep working, but nobody can run or edit it again.",
  "build.deleted": "Deleted “{title}”",

  "build.list.title": "Your scenarios",
  "build.list.lead":
    "Copy a catalogue scenario to make it yours, or import one exported from another deployment. Everything starts private.",
  "build.list.empty":
    "You have no scenarios of your own yet. Copy one from the catalogue — every translation comes with it — and change what your room needs.",
  "build.list.languages": "{done} of {total} languages",
  "build.list.languagesAll": "{count} languages",
  "build.list.updated": "Updated {when}",
  "build.new": "New from the catalogue",
  "build.new.lead": "Your copy starts as a private draft with every translation the original has.",
  "build.import": "Import JSON",
  "build.import.busy": "Importing…",
  "build.import.notJson": "That file is not JSON.",
  "build.import.invalidDocument": "That file is not an exported scenario.",
  "build.import.invalidScenario": "The scenario in that file does not pass the schema:",

  "build.missing.title": "Scenario not found",
  "build.missing.body": "It is not one of yours, or it has been deleted.",

  "build.tabs": "Editor sections",
  "build.tab.outline": "Outline",
  "build.tab.edit": "Edit",
  "build.tab.checks": "Checks",
  "build.outline": "Scenario outline",
  "build.outline.nodes": "{count, plural, one {# node} other {# nodes}}",
  "build.outline.unreachable": "Unreachable",
  "build.outline.errors": "{count, plural, one {# error} other {# errors}}",
  "build.outline.warnings": "{count, plural, one {# warning} other {# warnings}}",
  "build.section.overview": "Overview",
  "build.section.roles": "Roles",
  "build.section.scoring": "Scoring",
  "build.section.injects": "Injects",
  "build.section.badges": "Badges",
  "build.lead.overview": "What the picker shows, who the scenario is for and what it teaches.",
  "build.lead.roles": "Two or more seats. Give every role something only they know at the moment it matters.",
  "build.lead.scoring":
    "Dimensions are what the report scores; variables are the state the story branches on.",
  "build.lead.injects": "Curveballs the facilitator can throw from the console. Two or three is plenty.",
  "build.lead.badges": "Awards for players and the whole team, decided by the conditions below.",

  "build.save.saved": "Saved · {time}",
  "build.save.savedPlain": "Saved",
  "build.save.saving": "Saving…",
  "build.save.dirty": "Unsaved changes",
  "build.save.invalid": "Not saved — fix the errors",
  "build.save.error": "Not saved — connection problem",
  "build.save.conflict": "Not saved — changed elsewhere",
  "build.backup.found": "This tab kept edits from {time} that were never saved.",
  "build.backup.restore": "Restore them",
  "build.backup.discard": "Discard",
  "build.conflict.title": "Saved somewhere else",
  "build.conflict.body": "This scenario was saved from another tab or window at {time}, after you opened it.",
  "build.conflict.bodyPlain": "This scenario was saved from another tab or window after you opened it.",
  "build.conflict.reload": "Reload theirs",
  "build.conflict.keep": "Keep mine and save anyway",

  "build.publish": "Publish",
  "build.publish.busy": "Publishing…",
  "build.publish.done": "Published version {version}",
  "build.publish.blocked": "Publishing is blocked until the errors are fixed.",
  "build.publish.saveFirst": "The draft could not be saved, so it was not published.",
  "build.publish.errors":
    "{count, plural, one {Fix # error before publishing} other {Fix # errors before publishing}}",
  "build.publish.ready": "Ready to publish",

  "build.type.narrative": "Narrative",
  "build.type.call": "Call",
  "build.type.decision": "Decision",
  "build.type.poll": "Poll",
  "build.type.reveal": "Reveal",
  "build.type.branch": "Branch",
  "build.type.debrief": "Debrief",
  "build.type.narrative.help": "Scene-setting text, optionally with an email, text or ticket card.",
  "build.type.call.help": "A phone call revealed line by line on the big screen.",
  "build.type.decision.help": "The room votes between two to six options, each scored.",
  "build.type.poll.help": "An opinion question with nothing scored.",
  "build.type.reveal.help": "The teaching moment: what really happened and the lessons.",
  "build.type.branch.help": "Routes on variables instantly, with no screen of its own.",
  "build.type.debrief.help": "The end: moves the session to its debrief.",
  "build.node.add": "Add node",
  "build.node.addTitle": "Add a node",
  "build.node.isStart": "Start",
  "build.node.setStart": "Set as start",
  "build.node.duplicate": "Duplicate",
  "build.node.deleteTitle": "Delete node",
  "build.node.deleteStart":
    "This is the start node, so the exercise would have nowhere to begin. Set another node as the start first, then delete this one.",
  "build.node.deleteConfirm":
    "{count, plural, =0 {Delete “{title}”? Nothing points at it.} one {Delete “{title}”? # reference will point at nothing until you change it.} other {Delete “{title}”? # references will point at nothing until you change them.}}",

  "build.group.basics": "Basics",
  "build.group.content": "Content",
  "build.group.notes": "Private notes",
  "build.group.artifact": "Artefact card",
  "build.group.caller": "Caller",
  "build.group.callee": "Callee",
  "build.group.transcript": "Transcript",
  "build.group.afterCall": "After the call",
  "build.group.vote": "The vote",
  "build.group.options": "Options",
  "build.group.poll": "The question",
  "build.group.conditions": "Conditions",
  "build.group.catalogue": "In the catalogue",
  "build.group.audience": "Audience",
  "build.group.teaching": "What it teaches",
  "build.group.sources": "Modelled on",
  "build.group.dimensions": "Dimensions",
  "build.group.variables": "Variables",

  "build.field.none": "(none)",
  "build.field.numberRequired": "Enter a number.",
  "build.field.nodeId": "Node id",
  "build.field.title": "Title",
  "build.field.body": "Body",
  "build.field.next": "Next node",
  "build.field.facilitatorNotes": "Facilitator notes",
  "build.field.roleNotes": "Role notes",
  "build.field.artifactOn": "Show an artefact card",
  "build.field.artifactKind": "Kind",
  "build.field.artifactBody": "Card body",
  "build.field.artifact.from": "From",
  "build.field.artifact.to": "To",
  "build.field.artifact.subject": "Subject",
  "build.field.artifact.timestamp": "Time stamp",
  "build.artifact.email": "Email",
  "build.artifact.sms": "Text message",
  "build.artifact.chat": "Chat",
  "build.artifact.ticket": "Helpdesk ticket",
  "build.artifact.news": "News article",
  "build.artifact.memo": "Memo",
  "build.artifact.alert": "System alert",
  "build.field.callerName": "Caller name",
  "build.field.displayNumber": "Number on the display",
  "build.field.spoofed": "The number is spoofed",
  "build.field.voice": "Voice",
  "build.field.calleeName": "Callee name",
  "build.field.calleeRole": "Callee role",
  "build.transcript.add": "Add line",
  "build.transcript.line": "Line {n}",
  "build.field.speaker": "Speaker",
  "build.speaker.caller": "Caller",
  "build.speaker.callee": "Callee",
  "build.speaker.system": "Stage direction",
  "build.field.speakerName": "Name shown",
  "build.field.lineText": "What is said",
  "build.field.tell": "Tell",
  "build.field.pauseMs": "Pause after (ms)",
  "build.field.redFlags": "Red flags",
  "build.field.prompt": "Prompt",
  "build.field.context": "Context",
  "build.field.timerSec": "Timer (seconds)",
  "build.field.votersAll": "Everyone votes",
  "build.field.voters": "Roles that vote",
  "build.option.add": "Add option",
  "build.option.n": "Option {n}",
  "build.option.bestPossible": "Best possible here:",
  "build.option.bestPossibleNone": "no dimension is scored yet",
  "build.field.optionId": "Option id",
  "build.field.optionLabel": "Label",
  "build.field.optionDetail": "Detail",
  "build.field.quality": "Quality",
  "build.field.feedback": "Feedback",
  "build.field.score": "Score per dimension",
  "build.field.vars": "Variable changes",
  "build.field.roleBonus": "Role bonus",
  "build.field.tags": "Tags",
  "build.field.lessons": "Lessons",
  "build.condition.add": "Add condition",
  "build.condition.n": "Condition {n}",
  "build.condition.noVariables":
    "Branches route on variables, and this scenario has none. Add one under Scoring.",
  "build.field.var": "Variable",
  "build.field.op": "Test",
  "build.field.value": "Value",
  "build.field.else": "Otherwise go to",
  "build.field.tagline": "Tagline",
  "build.field.summary": "Summary",
  "build.field.difficulty": "Level",
  "build.field.minutes": "Length (minutes)",
  "build.field.version": "Version",
  "build.field.audience": "Track",
  "build.field.audienceNote": "Who exactly",
  "build.field.objectives": "Learning objectives",
  "build.field.start": "Start node",
  "build.source.add": "Add source",
  "build.source.n": "Source {n}",
  "build.field.sourceName": "Name",
  "build.field.sourceYear": "Year",
  "build.field.sourceUrl": "Link",
  "build.role.add": "Add role",
  "build.field.roleId": "Role id",
  "build.field.roleName": "Name",
  "build.field.icon": "Icon",
  "build.field.maxPlayers": "Most players",
  "build.field.brief": "Brief",
  "build.field.roleObjectives": "Objectives",
  "build.dimension.add": "Add dimension",
  "build.field.dimensionId": "Dimension id",
  "build.field.dimensionLabel": "Label",
  "build.field.description": "Description",
  "build.variable.add": "Add variable",
  "build.field.variableId": "Variable",
  "build.field.variableStart": "Starts at",
  "build.inject.add": "Add inject",
  "build.inject.addAfter": "Add a node",
  "build.inject.empty": "No injects yet.",
  "build.field.injectId": "Inject id",
  "build.field.injectNode": "Jumps to",
  "build.field.availableAfter": "Offered after",
  "build.badge.add": "Add badge",
  "build.badge.empty": "No badges yet.",
  "build.field.badgeId": "Badge id",
  "build.field.badgeName": "Name",
  "build.field.condition": "Earned when",
  "build.badge.kind.optionTag": "Chose tagged options",
  "build.badge.kind.speed": "Voted fast",
  "build.badge.kind.consensus": "Voted with the room",
  "build.badge.kind.bestPicks": "Picked the best option",
  "build.badge.kind.teamVarBelow": "Team kept a variable low",
  "build.badge.kind.teamDimension": "Team scored a dimension",
  "build.badge.help.optionTag": "A player chose options carrying this tag at least this many times.",
  "build.badge.help.speed": "A player's average time to vote stayed under this fraction of the timer.",
  "build.badge.help.consensus": "A player voted with the winning option at least this fraction of the time.",
  "build.badge.help.bestPicks": "A player picked the best option on at least this many decisions.",
  "build.badge.help.teamVarBelow":
    "Everyone earns it when the variable's peak stayed under the threshold all game.",
  "build.badge.help.teamDimension":
    "Everyone earns it when the team's final score in the dimension reaches this percentage.",
  "build.field.tag": "Tag",
  "build.field.minTimes": "At least",
  "build.field.fraction": "Fraction (0–1)",
  "build.field.minVotes": "Minimum votes",
  "build.field.below": "Stayed under",
  "build.field.dimension": "Dimension",
  "build.field.minPercent": "At least (%)",

  "build.help.nodeId":
    "Renaming updates them all; translated text for this node will need translating again.",
  "build.help.nodeTitle": "Shown to the facilitator in the console and at the top of the big screen.",
  "build.help.facilitatorNotes": "Only the facilitator sees these, beside the node in the console.",
  "build.help.roleNotes":
    "Private text shown only to players holding each role; leave a role empty for none.",
  "build.help.narrativeBody": "Scene-setting text for the big screen and the phones.",
  "build.help.debriefBody": "The closing words; reaching this node moves the session to its debrief.",
  "build.help.revealBody": "What really happened, told plainly.",
  "build.help.next": "Where the facilitator goes after this node; none ends the path here.",
  "build.help.artifactOn": "A card that looks like what the room would really see.",
  "build.help.artifactKind":
    "Pick the chrome the room would recognise: an email looks like webmail, a text like a phone thread.",
  "build.help.artifactBody": "In a text message or chat, a blank line starts a new bubble.",
  "build.help.displayNumber": "What the phone shows, which is often not who is calling.",
  "build.help.spoofed": "Revealed after the call: the number on the display was faked.",
  "build.help.voice": "A hint for the reveal, such as a synthetic clone of the finance director.",
  "build.help.calleeRole": "The role taking the call, if one does.",
  "build.help.transcript": "Revealed line by line on the big screen; the room hears it in this order.",
  "build.help.speakerName": "Leave empty to use the caller's or callee's name.",
  "build.help.tell": "The manipulation hiding in this line, revealed after the call and in the report.",
  "build.help.pauseMs": "How long the big screen holds before the next line; empty uses the default.",
  "build.help.redFlags": "The warning signs in this call, listed at the debrief.",
  "build.help.prompt": "The question the room votes on.",
  "build.help.pollPrompt": "An opinion question; answers are shown but never scored.",
  "build.help.context": "Optional background shown under the prompt.",
  "build.help.timerSec": "The default voting window, 10 to 900 seconds; the facilitator can extend it.",
  "build.help.voters": "Everyone with a seat, or only the roles whose call it is.",
  "build.help.options":
    "Mark exactly one best, and make the bad option the one a rushed room would pick: tied votes go to the worse option.",
  "build.help.optionDetail": "Optional, shown under the label.",
  "build.help.optionNext": "Where the story goes if this option wins.",
  "build.help.feedback": "Shown to everyone at the reveal; say why, not only what.",
  "build.help.score": "Score only the dimensions this decision is really about, highest for the best option.",
  "build.help.vars": "Changes to the variables that branches route on.",
  "build.help.roleBonus":
    "Players in these roles earn 25 extra points for choosing it, because it is their job.",
  "build.help.optionTags": "Used by badges, for example verify or slow-down.",
  "build.help.lessons": "One lesson per line, shown as a list.",
  "build.help.conditions": "Checked in order; the first that holds decides where the story goes.",
  "build.help.else": "Where the story goes when no condition holds.",
  "build.help.title": "The name facilitators see in the picker and players see on the big screen.",
  "build.help.tagline": "One line under the title in the picker.",
  "build.help.summary": "The paragraph a facilitator reads before choosing it.",
  "build.help.minutes": "10 to 240; forty to sixty minutes suits four or five decisions.",
  "build.help.version": "Change it when the content changes; publishing again bumps it for you.",
  "build.help.audience": "Decides where the picker files it and which vocabulary it uses.",
  "build.help.audienceNote": "Who exactly, in a facilitator's words, such as “Ages 12–16”.",
  "build.help.objectives": "What the room should be able to do afterwards; three or more is the convention.",
  "build.help.tags": "Free-form; the threat tags on a scenario card are mapped from these.",
  "build.help.start": "The first node of the exercise.",
  "build.help.basedOn": "The real incidents or reports the scenario is modelled on; the report cites them.",
  "build.help.icon": "A Lucide icon name, such as headset or landmark.",
  "build.help.iconUnknown": "Not an icon this app knows, so players see “{fallback}”.",
  "build.help.maxPlayers": "Leave empty for no limit.",
  "build.help.brief": "What a player holding this role knows and is responsible for.",
  "build.help.dimensions":
    "Two to eight; the readiness score is the mean of the ones the room was scored on.",
  "build.help.variables": "Numbers the story branches on; the value is where each one starts.",
  "build.help.injectDescription": "What the facilitator reads before throwing it in.",
  "build.help.injectNode":
    "Usually a node nothing else reaches; its next nodes must lead back into the story.",
  "build.help.availableAfter": "Offered once any of these nodes has been visited.",
  "build.help.availableAlways": "Offered at any time; add a node to hold it back.",

  "build.id.refs":
    "{count, plural, =0 {Nothing points at it yet.} one {Used by # reference.} other {Used by # references.}}",
  "build.id.invalid": "Use lowercase letters, digits and single hyphens, like call-back.",
  "build.id.invalidVar":
    "Start with a letter and use only letters, digits and underscores, like moneyAtRisk.",
  "build.id.taken": "“{id}” is already in use.",
  "build.list.add": "Add {item}",
  "build.list.up": "Move {item} up",
  "build.list.down": "Move {item} down",
  "build.list.remove": "Remove {item}",
  "build.list.none": "None yet.",
  "build.item.objective": "objective",
  "build.item.tag": "tag",
  "build.item.redFlag": "red flag",
  "build.item.lesson": "lesson",
  "build.kv.unknown": "“{id}” no longer exists",
  "build.kv.clear": "Clear",
  "build.kv.noDimensions": "Add dimensions under Scoring first.",
  "build.kv.noVariables": "No variables; add one under Scoring if the story should branch.",
  "build.roleNotes.unknown": "A note for “{id}”, which is not a role any more.",

  "build.checks.title": "Checks",
  "build.checks.errors": "Errors",
  "build.checks.warnings": "Warnings",
  "build.checks.conventions": "Conventions",
  "build.checks.noErrors": "No errors. It can be published.",
  "build.checks.noWarnings": "No warnings.",
  "build.checks.noConventions": "Every catalogue convention is met.",
  "build.play.title": "Playthrough",
  "build.play.help": "Two reference rooms play it: one always picks the best option, one always the worst.",
  "build.play.best": "Best room's readiness",
  "build.play.worst": "Worst room's readiness",
  "build.play.decisions": "Decisions on the best path",
  "build.play.atLeast": "at least {n}",
  "build.play.atMost": "at most {n}",
  "build.play.stuck": "never ends",
  "build.play.unexercised": "Never scored on the best path",
  "build.play.allExercised": "Every dimension is scored on the best path",
  "build.play.unavailable": "The playthrough runs once the scenario passes the schema.",
  "build.translate.title": "Translations",
  "build.translate.model": "Translated by {model}. Each run saves as it goes.",
  "build.translate.unavailable": "This deployment has no translation model configured.",
  "build.translate.all": "Translate all",
  "build.translate.one": "Translate",
  "build.translate.stop": "Stop",
  "build.translate.running": "Translating…",
  "build.translate.current": "Current",
  "build.translate.delta": "{missing} missing · {outdated} outdated",
  "build.translate.coverage": "{locale} coverage",
  "build.translate.total": "{count, plural, one {# string translated} other {# strings translated}}",
  "build.translate.noProgress":
    "{count, plural, one {The model's answer failed the checks (# rejected).} other {The model's answers failed the checks (# rejected).}}",
  "build.translate.rateLimited": "Too many translation runs this hour. Try again later.",
  "build.translate.saveFirst": "Fix the errors so the draft can be saved, then translate.",
  "build.translate.remove": "Remove the {locale} translation",
  "build.translate.removeTitle": "Remove translation",
  "build.translate.removeConfirm":
    "Remove every {locale} string of this scenario? Players choosing {locale} will see English until it is translated again.",
  "build.share.title": "Sharing",
  "build.share.visibility": "Who can run it",
  "build.share.help.private": "Only you, from your own picker.",
  "build.share.help.unlisted": "Any facilitator with the link below.",
  "build.share.help.listed": "Everyone, under Community in the picker, once an administrator approves it.",
  "build.share.link": "Run link",
  "build.share.publishFirst": "Others run the published version, so publish it before you share the link.",
  "build.share.approved": "Approved: it is listed under Community.",
  "build.share.pending": "Waiting for an administrator's approval; until then it works by link only.",
  "build.danger.title": "Delete",
  "build.danger.help": "Past sessions and reports keep working. The scenario itself cannot be restored.",

  // Scenario metadata
  "scenario.difficultyLabel": "Level",
  "scenario.lengthLabel": "Length",
  "scenario.rolesLabel": "Roles",
  "scenario.decisionsLabel": "Decisions",
  "scenario.difficulty.intro": "Introductory",
  "scenario.difficulty.standard": "Standard",
  "scenario.difficulty.advanced": "Advanced",
  "scenario.minutes": "{count} min",
  "scenario.roles": "{count, plural, one {# role} other {# roles}}",
  "scenario.decisions": "{count, plural, one {# decision} other {# decisions}}",
  "scenario.injects": "{count, plural, one {# inject} other {# injects}}",
  "scenario.basedOn": "Modelled on",
  "scenario.objectives": "Learning objectives",
  "scenario.translation.partial": "Partial translation ({percent}) — untranslated text is shown in English.",
  "scenario.translation.machine": "Translated content",

  // Outcome quality labels
  "quality.best": "Best practice",
  "quality.good": "Good",
  "quality.risky": "Risky",
  "quality.bad": "Harmful",

  // Toasts
  "toast.inject": "Inject: {title}",
  "toast.voteClosed": "Voting closed",
  "toast.copied": "Link copied",
  "toast.connectionLost": "Connection lost — trying to reconnect",
  "toast.reconnected": "Reconnected",
  "toast.roleAssigned": "Your role is now {role}",

  // Errors
  "error.generic": "Something went wrong.",
  "error.network": "Network problem. Check your connection.",
  "error.unauthorized": "Please sign in again.",
  "error.notFound": "Not found.",
  "error.title": "Something went wrong",
  "error.body": "Reload the page. If it keeps happening, tell your facilitator.",
  "error.reload": "Reload",

  // Time
  "time.seconds": "{count, plural, one {# second} other {# seconds}}",
  "time.minutes": "{count, plural, one {# minute} other {# minutes}}",
  "time.justNow": "just now",
};

export default en;
